import { useCallback } from 'react';
import { DiscountCodeData } from '@app/domain/model/eshop/transport/DiscountCodeData';
import { validateDiscountCode } from '@app/service/eshop/checkout/actions/validateDiscountCode';
import { ProductCode } from '@app/domain/model/eshop/transport/ProductCode';
import { PA_INSURANCE_STEPS } from '@app/app/esales/personal-accidents/_sections/steps/PA_INSURANCE_STEPS';
import { FormStatus } from '@app/lib/form/contracts/FormStatus';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchasePersonalInsuranceResult } from '@app/service/eshop/personal-accidents/actions/purchasePersonalAccidentsInsurance';

export function usePersonalAccidentsWizardCallbacks(
    discountCodeData: DiscountCodeData,
    setDiscountCodeData: (data: DiscountCodeData) => void,
    setCurrentStepKeyRef: React.MutableRefObject<Nullable<(key: string) => void>>,
    formState: FormState<PurchasePersonalInsuranceResult>,
) {
    const handleDiscountCode = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setDiscountCodeData({
                ...discountCodeData,
                discountCode: e.target.value,
                isInvalid: false,
                isSuccess: false,
                validTravelCode: undefined,
            });
        },
        [discountCodeData, setDiscountCodeData],
    );

    const handleApplyDiscountClick = useCallback(async () => {
        const discountCodeResult = await validateDiscountCode(
            discountCodeData.discountCode,
            ProductCode.PersonalAccidents,
            '',
        );

        const isTravelValid = discountCodeResult?.isValid === true;

        if (isTravelValid) {
            setDiscountCodeData({
                ...discountCodeData,
                isSuccess: true,
                isInvalid: false,
                validTravelCode: discountCodeData.discountCode,
            });
        } else {
            setDiscountCodeData({
                ...discountCodeData,
                isSuccess: false,
                isInvalid: true,
                validTravelCode: undefined,
            });
        }
    }, [discountCodeData, setDiscountCodeData]);

    const handleEditPolicyholderInfo = useCallback(() => {
        if (setCurrentStepKeyRef.current) {
            setCurrentStepKeyRef.current(PA_INSURANCE_STEPS.POLICYHOLDER);
        }
        formState.status = FormStatus.Pending;
    }, [formState, setCurrentStepKeyRef]);

    const handleEditInsuredPeople = useCallback(() => {
        if (setCurrentStepKeyRef.current) {
            setCurrentStepKeyRef.current(PA_INSURANCE_STEPS.INSURED_PERSONS);
        }
        formState.status = FormStatus.Pending;
    }, [formState, setCurrentStepKeyRef]);

    const handleEditChooseInsurance = useCallback(() => {
        if (setCurrentStepKeyRef.current) {
            setCurrentStepKeyRef.current(PA_INSURANCE_STEPS.CHOOSE_INSURANCE);
        }
        formState.status = FormStatus.Pending;
    }, [formState, setCurrentStepKeyRef]);

    const handleEditBeginDate = useCallback(() => {
        if (setCurrentStepKeyRef.current) {
            setCurrentStepKeyRef.current(PA_INSURANCE_STEPS.BEGIN_DATE);
        }
        formState.status = FormStatus.Pending;
    }, [formState, setCurrentStepKeyRef]);

    const onSuccessfulLogin = useCallback(() => {
        setCurrentStepKeyRef.current?.(PA_INSURANCE_STEPS.POLICYHOLDER);
    }, [setCurrentStepKeyRef]);

    return {
        handleDiscountCode,
        handleApplyDiscountClick,
        handleEditPolicyholderInfo,
        handleEditInsuredPeople,
        handleEditChooseInsurance,
        handleEditBeginDate,
        onSuccessfulLogin,
    };
}
